import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { MonthlyFinanceApiService } from './monthly-finance-api.service';
import { MonthlyFinanceQueriesService } from './monthly-finance-queries.service';
import {
  MonthlyFinanceOverrideEntry,
  MonthlyFinanceProfitAndLoss,
} from 'src/models/monthly-finance.model';
import { ParsedIncomeEstimate } from 'src/shared/util/income-estimate-pdf-parser';
import { buildMonthlyProjection } from 'src/shared/util/income-estimate-projection-rules';

export interface ApplyProjectionResult {
  /** Override entries written. */
  applied: number;
  /** Cells skipped because they already had a manual override. */
  skipped: number;
}

interface WindowMonth {
  year: number;
  month: number;     // 1-12
  calendarIndex: number; // 0-11 (month - 1)
}

/**
 * Applies a parsed TIDY income estimate as 12 months of *projected* P&L
 * overrides. This is the only piece that touches app state — the parser and the
 * distribution rules stay pure.
 */
@Injectable({ providedIn: 'root' })
export class ProjectionImportService {
  private readonly api = inject(MonthlyFinanceApiService);
  private readonly monthlyFinance = inject(MonthlyFinanceQueriesService);

  /**
   * Write the projection across the 12 consecutive months starting at
   * `startYear`/`startMonth`. Existing manual overrides are left untouched.
   */
  async applyProjection(
    parsed: ParsedIncomeEstimate,
    addressId: number,
    startYear: number,
    startMonth: number,
  ): Promise<ApplyProjectionResult> {
    const window = this.buildWindow(startYear, startMonth);
    const alreadyOverridden = await this.fetchOverriddenCells(addressId, window);

    const lines = buildMonthlyProjection(parsed);
    const entries: MonthlyFinanceOverrideEntry[] = [];
    let skipped = 0;

    for (const line of lines) {
      for (const slot of window) {
        const amount = line.monthsByCalendar[slot.calendarIndex];
        if (!amount) {
          continue;
        }
        if (alreadyOverridden.has(this.cellKey(line.backendKey, slot.year, slot.month))) {
          skipped++;
          continue;
        }
        entries.push({
          address_id: addressId,
          monthly_finance_type_key: line.backendKey,
          kind: 'projected',
          year: slot.year,
          month: slot.month,
          overridden_amount: amount,
        });
      }
    }

    if (entries.length > 0) {
      // Reuse the existing mutation so its onSuccess invalidation (P&L, audits,
      // dashboard widgets) runs exactly as it does for manual cell edits.
      await this.monthlyFinance.overrideMutation.mutateAsync(entries);
    }

    return { applied: entries.length, skipped };
  }

  private buildWindow(startYear: number, startMonth: number): WindowMonth[] {
    const window: WindowMonth[] = [];
    for (let k = 0; k < 12; k++) {
      const m0 = startMonth - 1 + k;
      const year = startYear + Math.floor(m0 / 12);
      const month = (m0 % 12) + 1;
      window.push({ year, month, calendarIndex: month - 1 });
    }
    return window;
  }

  /** Set of `key:year:month` cells that already carry a manual projected override. */
  private async fetchOverriddenCells(
    addressId: number,
    window: WindowMonth[],
  ): Promise<Set<string>> {
    const first = window[0];
    const last = window[window.length - 1];
    let pl: MonthlyFinanceProfitAndLoss;
    try {
      pl = await lastValueFrom(
        this.api.getProfitAndLoss({
          start_year: first.year,
          start_month: first.month,
          end_year: last.year,
          end_month: last.month,
          address_id: addressId,
          kind: 'projected',
        }),
      );
    } catch {
      // Fail closed: if we can't read the existing projections we can't tell
      // which cells are manual overrides, so abort rather than risk clobbering
      // them by writing everything.
      throw new Error(
        "Couldn't check existing projections, so the import was cancelled to avoid overwriting manual edits. Please try again.",
      );
    }

    const overridden = new Set<string>();
    for (const category of pl?.categories ?? []) {
      for (const sub of category.subcategories) {
        for (const item of sub.items) {
          for (const value of item.values) {
            if (value.overridden_amount !== null) {
              overridden.add(this.cellKey(item.key, value.year, value.month));
            }
          }
        }
      }
    }
    return overridden;
  }

  private cellKey(key: string, year: number, month: number): string {
    return `${key}:${year}:${month}`;
  }
}
